import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { normalizeDashes } from '@/features/audiences/lib';
import { AGE_OPTIONS, GENDER_OPTIONS } from '@/features/audiences/model';
import { SelectTextField } from '@/shared/ui/form';
import { IdentityPlatformIcon } from '@/shared/ui/Icons';

import { FormSection, PersonaTextField } from '../../PersonaFormSections';
import { isFieldRequired } from '../EditPersonaForm.schema';
import { useEditPersonaFormContext } from '../hooks/useEditPersonaFormContext';

export function DemographicsSection() {
  const { t } = useTranslation('audiences');

  const { control } = useEditPersonaFormContext();

  return (
    <FormSection
      icon={<IdentityPlatformIcon />}
      title={t('audiences.demographicsSection.demographics')}
    >
      <div className="flex flex-col gap-6">
        <div className="grid grid-cols-[1fr_1fr_4fr] gap-4">
          <Controller
            name="age"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <SelectTextField
                {...field}
                label={t('audiences.demographicsSection.age')}
                placeholder={t('audiences.demographicsSection.selectAge')}
                required={isFieldRequired(field.name)}
                error={!!error}
                helperText={error?.message}
                options={AGE_OPTIONS}
              />
            )}
          />
          <Controller
            name="gender"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <SelectTextField
                {...field}
                label={t('audiences.demographicsSection.gender')}
                placeholder={t('audiences.demographicsSection.selectGender')}
                required={isFieldRequired(field.name)}
                error={!!error}
                helperText={error?.message}
                options={GENDER_OPTIONS}
              />
            )}
          />
          <Controller
            name="location"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <PersonaTextField
                {...field}
                label={t('audiences.demographicsSection.location')}
                placeholder={t('audiences.demographicsSection.eGUrbanLivesIn')}
                required={isFieldRequired(field.name)}
                error={error?.message}
              />
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Controller
            name="education"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <PersonaTextField
                {...field}
                label={t('audiences.demographicsSection.education')}
                placeholder={t('audiences.demographicsSection.eGUniversityDegree')}
                required={isFieldRequired(field.name)}
                error={error?.message}
              />
            )}
          />
          <Controller
            name="familyComposition"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <PersonaTextField
                {...field}
                label={t('audiences.demographicsSection.familyComposition')}
                placeholder={t('audiences.demographicsSection.eGLivesWithA')}
                required={isFieldRequired(field.name)}
                error={error?.message}
              />
            )}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Controller
            name="annualIncome"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <PersonaTextField
                {...field}
                onChange={(e) => field.onChange(normalizeDashes(e.target.value))}
                label={t('audiences.demographicsSection.annualIncome')}
                placeholder={t('audiences.personaFieldsConstants.eG7500010000')}
                required={isFieldRequired(field.name)}
                error={error?.message}
              />
            )}
          />
          <Controller
            name="lifeStage"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <PersonaTextField
                {...field}
                label={t('audiences.demographicsSection.lifeStage')}
                placeholder={t('audiences.demographicsSection.eGEarlyCareerProfessional')}
                required={isFieldRequired(field.name)}
                error={error?.message}
              />
            )}
          />
        </div>
      </div>
    </FormSection>
  );
}
